/**
 * TypeRunner — see prompt, type the answer under a per-question timer.
 * Simple, keyboard-first. Works on mobile via the native text input.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuizGameSpec } from "@/types/quizGameSpec";
import type { GameRunResult } from "@/types/gameSpec";
import { GameHud } from "./hud/GameHud";
import { attemptsToRunResult, type ArchetypeAttempt } from "./shared";

interface Props {
  spec: QuizGameSpec;
  onComplete: (r: GameRunResult) => void;
  onExit?: () => void;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

export const TypeRunner = ({ spec, onComplete, onExit }: Props) => {
  const { theme, questions, tuning } = spec;
  const total = questions.length;
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(tuning.lives);
  const [timeLeft, setTimeLeft] = useState(tuning.timer_s);
  const [qTimeLeft, setQTimeLeft] = useState(tuning.question_time_s);
  const [streak, setStreak] = useState(0);
  const [streakMax, setStreakMax] = useState(0);
  const [flash, setFlash] = useState<null | "good" | "bad">(null);
  const [finished, setFinished] = useState(false);
  const attemptsRef = useRef<ArchetypeAttempt[]>([]);
  const startedAt = useRef(Date.now());
  const questionStartedAt = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);

  const current = questions[idx];

  useEffect(() => { inputRef.current?.focus(); }, [idx]);

  useEffect(() => {
    if (finished) return;
    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { window.clearInterval(id); finish(false); return 0; }
        return t - 1;
      });
      setQTimeLeft((qt) => {
        if (qt <= 1) { submit(true); return tuning.question_time_s; }
        return qt - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, idx]);

  function submit(timedOut = false) {
    if (finished || !current) return;
    const given = answer;
    const isCorrect = !timedOut && norm(given) === norm(current.correct);
    attemptsRef.current.push({
      prompt: current.prompt, correct: current.correct, answered: given || "(timeout)",
      isCorrect, timeMs: Date.now() - questionStartedAt.current,
    });
    if (isCorrect) {
      setScore((s) => s + 1);
      setStreak((s) => { const n = s + 1; setStreakMax((m) => Math.max(m, n)); return n; });
      setFlash("good");
    } else {
      setLives((l) => { const n = l - 1; if (n <= 0) { finish(false); return n; } return n; });
      setStreak(0);
      setFlash("bad");
    }
    setAnswer("");
    setTimeout(() => {
      setFlash(null);
      if (idx + 1 >= total) finish(true);
      else {
        setIdx((i) => i + 1);
        setQTimeLeft(tuning.question_time_s);
        questionStartedAt.current = Date.now();
      }
    }, 500);
  }

  function finish(won: boolean) {
    if (finished) return;
    setFinished(true);
    onComplete(attemptsToRunResult({
      archetype: "type-runner", attempts: attemptsRef.current, startedAt: startedAt.current,
      won: won && lives > 0, score, maxScore: total, streakMax,
    }));
  }

  const progress = Math.round((idx / Math.max(1, total)) * 100);
  const flashColor = flash === "good" ? theme.palette.good : flash === "bad" ? theme.palette.bad : theme.palette.surface;

  return (
    <div className="space-y-3">
      <GameHud title={theme.title} score={score} maxScore={total} lives={lives} timeLeft={timeLeft} progress={progress} streak={streak} />

      <div className="rounded-lg p-6 flex flex-col items-center justify-center" style={{ background: theme.palette.bg, color: "#fff", minHeight: 380 }}>
        <div className="text-sm opacity-80 mb-1">Type the answer</div>
        <div className="text-xs opacity-60 mb-4">{qTimeLeft}s left for this question</div>
        <AnimatePresence mode="wait">
          <motion.div
            key={current?.prompt ?? "done"}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="px-6 py-5 rounded-lg text-2xl font-bold mb-6 max-w-full text-center"
            style={{ background: flashColor, border: `2px solid ${theme.palette.accent}`, transition: "background .2s" }}
          >
            {current?.prompt ?? "Done!"}
          </motion.div>
        </AnimatePresence>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="w-full max-w-sm flex gap-2"
        >
          <Input
            ref={inputRef}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Your answer"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={finished}
            className="text-lg"
          />
          <Button type="submit" disabled={finished || !answer}>Go</Button>
        </form>
      </div>

      {onExit && <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={onExit}>Exit game</Button></div>}
    </div>
  );
};

export default TypeRunner;
